import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import { basicSetup, EditorView } from "codemirror";
import { EditorState } from "@codemirror/state";
import { python } from "@codemirror/lang-python";
import { isApiConfigured } from "../auth-config";
import {
  postStudentDemoMarkDone,
  postStudentLessonSubmission,
  postStudentLessonWorkspaceReset,
  type DemoMarkDoneResponse,
} from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import {
  SIGN_IN_TO_CHECK_MESSAGE,
  userFacingRequestError,
} from "../auth/session-keepalive";
import {
  LAB_PROGRESS_HINTS,
  type AssignmentProgressStatus,
} from "../student/assignmentProgressStatus";
import { ProgressStatusChip } from "../student/ProgressStatusChip";
import { celebratePassing } from "./confettiCelebration";
import type { LessonTestBlockWorkerResult } from "../python-runtime/protocol";
import { usePythonRuntime } from "../python-runtime/usePythonRuntime";
import { IdeOutputPanel } from "../python-runtime/IdeOutputPanel";
import { resolveEditorInitialSource } from "./editorInitialSource";
import { normalizePythonIndentation } from "./normalizePythonIndentation";
import { starterFilesFromMainPy } from "../teacher/course-template-helpers";
import { pythonEditorExtensions } from "./pythonEditorConfig";
import { StudentIdeTourPanel } from "./StudentIdeTourPanel";
import type { DemoTourStep } from "./demoTourSteps";
import type { LessonImmersiveTheme } from "../teacher/lessonImmersiveTheme";

const ideEditorTheme = EditorView.theme(
  {
    "&": {
      height: "100%",
      backgroundColor: "var(--ide-editor-bg)",
      color: "var(--ide-editor-fg)",
    },
    ".cm-scroller": {
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
      fontSize: "0.9rem",
      lineHeight: 1.45,
    },
    ".cm-content": { caretColor: "var(--color-forest)" },
    ".cm-gutters": {
      backgroundColor: "rgba(27, 77, 46, 0.07)",
      color: "var(--color-brown)",
      borderRight: "1px solid rgba(27, 77, 46, 0.12)",
    },
    ".cm-activeLineGutter": {
      backgroundColor: "rgba(107, 163, 54, 0.12)",
    },
  },
  { dark: false },
);

type SaveStatus = "saved" | "unsaved" | "saving" | "error";

type MarkDoneModal =
  | null
  | { kind: "result"; result: DemoMarkDoneResponse; submissionSaveError?: string }
  | { kind: "error"; message: string };

const AUTOSAVE_DEBOUNCE_MS = 450;
const MIN_SAVING_VISIBLE_MS = 120;

function truncateOneLine(s: string, max: number): string {
  const t = s.replace(/\s+/g, " ").trim();
  return t.length <= max ? t : `${t.slice(0, max - 1)}…`;
}

/** Specs for browser-run lesson checks (testCode is never rendered in the UI). */
export type LessonTestVisibilityLevel = "hidden" | "summary" | "detailed";

export type LessonRuntimeMarkDoneSpec = {
  readonly id: string;
  readonly name: string;
  readonly testCode: string;
  readonly isRequired: boolean;
  readonly visibility: LessonTestVisibilityLevel;
  readonly description: string | null;
};

function blockFullyPassed(block: LessonTestBlockWorkerResult): boolean {
  if (block.loadError) return false;
  return block.cases.length > 0 && block.cases.every((c) => c.ok);
}

function primaryFailureTrace(block: LessonTestBlockWorkerResult): string {
  if (block.loadError) return block.loadError;
  const bad = block.cases.find((c) => !c.ok);
  return bad?.err ?? "";
}

function extractAssertHint(traceback: string): string {
  const t = traceback.trim();
  if (!t) return "";
  const m = /AssertionError:\s*([^\n\r]+)/i.exec(t);
  if (m?.[1]) return m[1].trim();
  const m2 = /AssertionError\s*\n\s*(.+)/im.exec(t);
  if (m2?.[1]) return m2[1].trim().split("\n")[0]?.trim() ?? "";
  const lines = t.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  return lines[lines.length - 1] ?? "";
}

function buildCheckLabel(
  spec: LessonRuntimeMarkDoneSpec,
  block: LessonTestBlockWorkerResult
): { id: string; label: string; passed: boolean } {
  if (blockFullyPassed(block)) {
    return { id: `lt-${spec.id}`, label: spec.name, passed: true };
  }
  const trace = primaryFailureTrace(block);
  const hint = extractAssertHint(trace);
  const teacherNote = (spec.description ?? "").trim();
  const vis = spec.visibility;
  if (vis === "hidden") {
    const tail = teacherNote ? ` ${teacherNote}` : " Adjust your code and run Check My Work again.";
    return { id: `lt-${spec.id}`, label: `${spec.name}: did not pass yet.${tail}`, passed: false };
  }
  if (vis === "summary") {
    const parts: string[] = [`${spec.name}:`];
    if (teacherNote) parts.push(teacherNote);
    if (hint && hint !== teacherNote) parts.push(`(${hint})`);
    else if (!teacherNote && !hint) parts.push("(review the lesson steps and your main.py)");
    else if (!teacherNote && hint) parts.push(`(${hint})`);
    return { id: `lt-${spec.id}`, label: parts.join(" "), passed: false };
  }
  const traceShort = truncateOneLine(trace, 220);
  const primary =
    (hint && hint !== teacherNote ? hint : "") ||
    (traceShort && traceShort !== teacherNote ? traceShort : "") ||
    teacherNote ||
    "Review your program against the lesson instructions.";
  const label =
    teacherNote && primary !== teacherNote ? `${spec.name}: ${teacherNote} (${primary})` : `${spec.name}: ${primary}`;
  return { id: `lt-${spec.id}`, label, passed: false };
}

function buildMarkDoneFromLessonTestBlocks(
  blocks: LessonTestBlockWorkerResult[],
  specs: readonly LessonRuntimeMarkDoneSpec[]
): DemoMarkDoneResponse {
  const byId = new Map(blocks.map((b) => [b.testId, b]));
  const checks = specs.map((s) => {
    const b = byId.get(s.id);
    if (!b) return { id: `lt-${s.id}`, label: `${s.name}: check did not run.`, passed: false };
    return buildCheckLabel(s, b);
  });
  const passed = checks.every((c) => c.passed);
  return {
    submissionId: `local-${crypto.randomUUID()}`,
    outcome: passed ? "passed" : "failed",
    summary: passed
      ? "All automated lesson checks passed."
      : "Some checks need attention. Read each line below, update main.py, then run Check My Work again.",
    checks,
    validationEngine: "lesson_autocheck_browser",
  };
}

function buildServerTestResults(
  blocks: LessonTestBlockWorkerResult[],
  specs: readonly LessonRuntimeMarkDoneSpec[]
): { lessonTestId: string; passed: boolean; message?: string }[] {
  const byId = new Map(blocks.map((b) => [b.testId, b]));
  return specs.map((s) => {
    const b = byId.get(s.id);
    const passed = b ? blockFullyPassed(b) : false;
    if (passed || s.visibility === "hidden") {
      return { lessonTestId: s.id, passed };
    }
    const trace = b ? primaryFailureTrace(b) : "";
    const msg =
      s.visibility === "summary"
        ? (extractAssertHint(trace).slice(0, 240) || (s.description ?? "").trim() || undefined)
        : truncateOneLine(trace, 500) || (s.description ?? "").trim() || undefined;
    return { lessonTestId: s.id, passed, ...(msg ? { message: msg } : {}) };
  });
}

function submissionFlagsFromBlocks(
  blocks: LessonTestBlockWorkerResult[],
  specs: readonly LessonRuntimeMarkDoneSpec[]
): { requiredPassed: boolean; optionalPassed: boolean } {
  const byId = new Map(blocks.map((b) => [b.testId, b]));
  const requiredPassed = specs.filter((s) => s.isRequired).every((s) => blockFullyPassed(byId.get(s.id)!));
  const optional = specs.filter((s) => !s.isRequired);
  const optionalPassed = optional.length === 0 || optional.every((s) => blockFullyPassed(byId.get(s.id)!));
  return { requiredPassed, optionalPassed };
}

/** @deprecated Use LessonRuntimeMarkDoneSpec */
export type TeacherPreviewLessonTestSpec = LessonRuntimeMarkDoneSpec;

export interface StudentIdeBodyProps {
  readonly eyebrow: string;
  readonly title: string;
  readonly backTo: string;
  readonly backLabel: string;
  readonly initialMainPy: string;
  readonly storageKey: string;
  readonly tourSteps?: readonly DemoTourStep[];
  /**
   * When set (including `[]`), Check My Work runs these lesson checks in Pyodide.
   * `testCode` must never be shown in the UI. Only pass/fail and hints derived from results.
   */
  readonly lessonTestsForMarkDone?: readonly LessonRuntimeMarkDoneSpec[];
  /** When set with `lessonTestsForMarkDone`, POST submission after checks (real assignments). */
  readonly markDoneSubmissionTarget?: { cohortId: string; lessonId: string };
  /** When set, enables Start over (reset to starter files) for open assignments. */
  readonly workspaceResetTarget?: { cohortId: string; lessonId: string };
  /** View-only mode (e.g. closed assignment review). Run still works; Check My Work hidden. */
  readonly readOnly?: boolean;
  /** Preview mode: allow editing but never read or write localStorage for this workspace. */
  readonly disableLocalPersist?: boolean;
  /** Shown in the lab header when working on a real assignment. */
  readonly assignmentProgressStatus?: AssignmentProgressStatus;
  /**
   * How the server chose `initialMainPy` (starter, last check, or workspace).
   * A draft already saved in this browser still wins, so a refresh cannot drop unsubmitted edits.
   */
  readonly editorBootstrap?: "starter" | "workspace" | "submission";
  /**
   * Teacher authoring (e.g. AI studio): keep the preview editor aligned with the starter
   * main.py field instead of a stale localStorage draft.
   */
  readonly authoringStarterMainPy?: string;
  /** Optional controls rendered in the IDE header (e.g. Edit lesson, Add to library). */
  readonly headerExtra?: ReactNode;
  /** When set, themes the page background with the lesson's colors + icon watermark and plays an intro. */
  readonly immersiveTheme?: LessonImmersiveTheme;
  /** "wash" plays the full takeover intro; "arrive" (launched via the cross-fade overlay) only fades content in. */
  readonly introMode?: "wash" | "arrive";
}

export function StudentIdeBody({
  eyebrow,
  title,
  backTo,
  backLabel,
  initialMainPy,
  storageKey,
  tourSteps,
  lessonTestsForMarkDone,
  markDoneSubmissionTarget,
  workspaceResetTarget,
  readOnly = false,
  disableLocalPersist = false,
  assignmentProgressStatus,
  authoringStarterMainPy,
  immersiveTheme,
  introMode = "wash",
  headerExtra,
}: StudentIdeBodyProps) {
  const { identity, getBearerToken } = useAuth();
  const bearerToken = getBearerToken();
  const arrive = Boolean(immersiveTheme) && introMode === "arrive";
  const [washing, setWashing] = useState(Boolean(immersiveTheme) && introMode === "wash");
  const [assignmentProgress, setAssignmentProgress] = useState<AssignmentProgressStatus | null>(
    assignmentProgressStatus ?? null
  );
  const editorHostRef = useRef<HTMLDivElement | null>(null);
  const skipLocalDraftRef = useRef(false);
  const editorRef = useRef<EditorView | null>(null);
  const consoleInputRef = useRef<HTMLInputElement | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("saved");
  const persistLocally = !readOnly && !disableLocalPersist;

  const {
    phase,
    runtimeError,
    lines,
    inputPrompt,
    canUseSharedMemory,
    turtleHtml,
    turtleTabVisible,
    outputTab,
    setOutputTab,
    run,
    stop,
    submitInput,
    clearConsole,
    runLessonTests,
  } = usePythonRuntime();

  const [inputDraft, setInputDraft] = useState("");
  const [markDoneBusy, setMarkDoneBusy] = useState(false);
  const [markDoneModal, setMarkDoneModal] = useState<MarkDoneModal>(null);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [editorEpoch, setEditorEpoch] = useState(0);
  const [forcedMainPy, setForcedMainPy] = useState<string | null>(null);
  const [clearConsoleOnRun, setClearConsoleOnRun] = useState(() => {
    try {
      return localStorage.getItem("ccs-student-ide-clear-console-on-run") === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (assignmentProgressStatus) setAssignmentProgress(assignmentProgressStatus);
  }, [assignmentProgressStatus]);

  useEffect(() => {
    const host = editorHostRef.current;
    if (!host) return;

    const serverPy = (forcedMainPy ?? initialMainPy).trim();
    const authoringPy = authoringStarterMainPy?.trim() ?? "";
    let localDraft: string | null = null;
    const skipLocalDraft = skipLocalDraftRef.current;
    skipLocalDraftRef.current = false;
    if (persistLocally && !authoringPy && !skipLocalDraft) {
      try {
        localDraft = localStorage.getItem(storageKey);
      } catch {
        localDraft = null;
      }
    }
    let initial = resolveEditorInitialSource({ authoringPy, localDraft, serverPy });
    if (initial.trim()) initial = normalizePythonIndentation(initial);

    let debounceTimer: ReturnType<typeof setTimeout> | undefined;
    let savingDoneTimer: ReturnType<typeof setTimeout> | undefined;

    const persistNow = (source: string) => {
      if (!persistLocally) return;
      if (savingDoneTimer) clearTimeout(savingDoneTimer);
      savingDoneTimer = undefined;
      setSaveStatus("saving");
      requestAnimationFrame(() => {
        try {
          localStorage.setItem(storageKey, source);
          savingDoneTimer = setTimeout(() => {
            savingDoneTimer = undefined;
            setSaveStatus("saved");
          }, MIN_SAVING_VISIBLE_MS);
        } catch {
          setSaveStatus("error");
        }
      });
    };

    const schedulePersist = (source: string) => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        debounceTimer = undefined;
        persistNow(source);
      }, AUTOSAVE_DEBOUNCE_MS);
    };

    const flushPersist = (source: string) => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = undefined;
      persistNow(source);
    };

    const view = new EditorView({
      state: EditorState.create({
        doc: initial,
        extensions: [
          basicSetup,
          python(),
          ...pythonEditorExtensions,
          ideEditorTheme,
          ...(readOnly ? [EditorView.editable.of(false), EditorState.readOnly.of(true)] : []),
          EditorView.lineWrapping,
          EditorView.updateListener.of((update) => {
            if (!update.docChanged || readOnly) return;
            if (persistLocally) {
              setSaveStatus("unsaved");
              schedulePersist(update.state.doc.toString());
            }
          }),
          EditorView.domEventHandlers({
            blur: (_, innerView) => {
              if (readOnly || !persistLocally) return false;
              flushPersist(innerView.state.doc.toString());
              return false;
            },
          }),
        ],
      }),
      parent: host,
    });
    editorRef.current = view;

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      if (savingDoneTimer) clearTimeout(savingDoneTimer);
      if (persistLocally && !skipLocalDraftRef.current) {
        try {
          localStorage.setItem(storageKey, view.state.doc.toString());
        } catch {
          /* quota / private mode */
        }
      }
      view.destroy();
      editorRef.current = null;
    };
  }, [authoringStarterMainPy, disableLocalPersist, editorEpoch, forcedMainPy, initialMainPy, persistLocally, readOnly, storageKey]);

  useEffect(() => {
    const next = authoringStarterMainPy?.trim();
    if (next === undefined) return;
    const view = editorRef.current;
    if (!view) return;
    const cur = view.state.doc.toString();
    if (cur === next) return;
    view.dispatch({
      changes: { from: 0, to: view.state.doc.length, insert: next },
    });
    if (persistLocally) {
      try {
        localStorage.setItem(storageKey, next);
      } catch {
        /* quota / private mode */
      }
    }
  }, [authoringStarterMainPy, persistLocally, storageKey]);

  const retrySave = useCallback(() => {
    if (!persistLocally) return;
    const view = editorRef.current;
    if (!view) return;
    setSaveStatus("saving");
    requestAnimationFrame(() => {
      try {
        localStorage.setItem(storageKey, view.state.doc.toString());
        setTimeout(() => setSaveStatus("saved"), MIN_SAVING_VISIBLE_MS);
      } catch {
        setSaveStatus("error");
      }
    });
  }, [persistLocally, storageKey]);

  useEffect(() => {
    if (phase === "awaiting_input") consoleInputRef.current?.focus();
  }, [phase]);

  useEffect(() => {
    try {
      localStorage.setItem("ccs-student-ide-clear-console-on-run", clearConsoleOnRun ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [clearConsoleOnRun]);

  useEffect(() => {
    if (!markDoneModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMarkDoneModal(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [markDoneModal]);

  const onMarkDone = useCallback(async () => {
    const view = editorRef.current;
    const token = getBearerToken();
    if (!view) return;
    if (!token) {
      setMarkDoneModal({ kind: "error", message: SIGN_IN_TO_CHECK_MESSAGE });
      return;
    }
    const src = view.state.doc.toString();
    if (persistLocally) {
      try {
        localStorage.setItem(storageKey, src);
      } catch {
        /* still submit */
      }
      setSaveStatus("saved");
    }
    setMarkDoneBusy(true);
    setMarkDoneModal(null);
    try {
      if (lessonTestsForMarkDone !== undefined) {
        if (lessonTestsForMarkDone.length === 0) {
          celebratePassing();
          setMarkDoneModal({
            kind: "result",
            result: {
              submissionId: `local-${crypto.randomUUID()}`,
              outcome: "passed",
              summary: "This lesson has no automated checks configured.",
              checks: [{ id: "no-lesson-tests", label: "No automated checks on this lesson", passed: true }],
              validationEngine: "lesson_autocheck_browser",
            },
          });
          return;
        }
        const blocks = await runLessonTests(
          src,
          lessonTestsForMarkDone.map((t) => ({ id: t.id, name: t.name, testCode: t.testCode }))
        );
        const result = buildMarkDoneFromLessonTestBlocks(blocks, lessonTestsForMarkDone);
        const flags = submissionFlagsFromBlocks(blocks, lessonTestsForMarkDone);
        let submissionSaveError: string | undefined;
        if (markDoneSubmissionTarget) {
          try {
            await postStudentLessonSubmission(
              token,
              markDoneSubmissionTarget.cohortId,
              markDoneSubmissionTarget.lessonId,
              {
                workspaceFiles: starterFilesFromMainPy(src),
                validationEngine: "browser_pyodide_lesson_tests_v1",
                overallStatus: result.outcome === "passed" ? "passed" : "failed",
                requiredPassed: flags.requiredPassed,
                optionalPassed: flags.optionalPassed,
                testResults: buildServerTestResults(blocks, lessonTestsForMarkDone),
                submissionKind: "normal",
              }
            );
            setAssignmentProgress((prev) => {
              if (flags.requiredPassed) return "complete";
              if (prev === "complete") return "complete";
              return "needs_help";
            });
            try {
              if (persistLocally) localStorage.setItem(storageKey, src);
            } catch {
              /* ignore */
            }
          } catch (e) {
            submissionSaveError = userFacingRequestError(
              e,
              "Could not save your attempt to the server (try again later).",
              SIGN_IN_TO_CHECK_MESSAGE
            );
          }
        }
        if (result.outcome === "passed" && flags.requiredPassed) {
          celebratePassing();
        }
        setMarkDoneModal({ kind: "result", result, submissionSaveError });
        return;
      }
      const result = await postStudentDemoMarkDone(token, src);
      if (result.outcome === "passed") {
        celebratePassing();
      }
      setMarkDoneModal({ kind: "result", result });
    } catch (err) {
      const message = userFacingRequestError(err, "Check My Work failed", SIGN_IN_TO_CHECK_MESSAGE);
      setMarkDoneModal({ kind: "error", message });
    } finally {
      setMarkDoneBusy(false);
    }
  }, [getBearerToken, lessonTestsForMarkDone, markDoneSubmissionTarget, persistLocally, runLessonTests, storageKey]);

  const onRun = useCallback(() => {
    const view = editorRef.current;
    if (!view) return;
    if (clearConsoleOnRun) clearConsole();
    run(view.state.doc.toString(), "main.py");
  }, [clearConsole, clearConsoleOnRun, run]);

  const onSubmitConsoleInput = useCallback(() => {
    submitInput(inputDraft);
    setInputDraft("");
  }, [inputDraft, submitInput]);

  const running = phase === "running" || phase === "awaiting_input";
  const loadingRuntime = phase === "loading" && canUseSharedMemory;

  const apiReady = isApiConfigured();
  const lessonTestMarkDone = lessonTestsForMarkDone !== undefined;
  const workspaceNote = readOnly
    ? "This assignment is closed. Your submitted code is shown read-only. You can still run it to review output."
    : disableLocalPersist
      ? "Preview mode. Run and edit freely; nothing is saved."
      : markDoneSubmissionTarget
        ? "Your code saves on this device as you type. Click Check My Work when you're ready to turn in this assignment."
        : "Your code saves on this device as you type, so you can keep testing ideas and come back later.";
  const markDoneDisabled =
    readOnly ||
    running ||
    loadingRuntime ||
    markDoneBusy ||
    !canUseSharedMemory ||
    (!lessonTestMarkDone && !apiReady);

  const markDoneHint = !bearerToken
    ? SIGN_IN_TO_CHECK_MESSAGE
    : !canUseSharedMemory
      ? "Python runtime is not ready. Fix cross-origin isolation or use HTTPS so lesson checks can run."
      : lessonTestMarkDone
        ? "Runs automated lesson checks on your main.py in the browser. You only see pass/fail and hints, not the test code."
        : !apiReady
          ? "Configure VITE_API_BASE_URL so Check My Work can reach the API."
          : undefined;

  const onConfirmReset = async () => {
    if (!workspaceResetTarget) return;
    if (!bearerToken) {
      setResetConfirmOpen(false);
      setMarkDoneModal({ kind: "error", message: SIGN_IN_TO_CHECK_MESSAGE });
      return;
    }
    setResetBusy(true);
    try {
      const result = await postStudentLessonWorkspaceReset(
        bearerToken,
        workspaceResetTarget.cohortId,
        workspaceResetTarget.lessonId
      );
      try {
        localStorage.removeItem(storageKey);
      } catch {
        /* ignore */
      }
      skipLocalDraftRef.current = true;
      setForcedMainPy(result.preview.mainPy);
      setEditorEpoch((n) => n + 1);
      setSaveStatus("saved");
      setResetConfirmOpen(false);
    } catch (e) {
      setMarkDoneModal({
        kind: "error",
        message: e instanceof Error ? e.message : "Could not reset project",
      });
      setResetConfirmOpen(false);
    } finally {
      setResetBusy(false);
    }
  };

  return (
    <div
      className={[
        "ide-page",
        immersiveTheme ? "ide-page--themed" : "",
        washing ? "ide-page--intro" : "",
        arrive ? "ide-page--arrive" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={immersiveTheme?.style}
      onAnimationEnd={
        washing
          ? (e) => {
              if (e.target === e.currentTarget) setWashing(false);
            }
          : undefined
      }
    >
      {immersiveTheme ? (
        <div className="ide-immersive-bg" aria-hidden="true">
          {immersiveTheme.iconDef ? (
            <FontAwesomeIcon icon={immersiveTheme.iconDef} className="ide-immersive-watermark" />
          ) : null}
        </div>
      ) : null}
      {assignmentProgress ? (
        <div
          className={`ide-assignment-progress ide-assignment-progress--${assignmentProgress}`}
          role="status"
          aria-live="polite"
        >
          <ProgressStatusChip status={assignmentProgress} className="ide-assignment-progress-chip" />
          <p className="ide-assignment-progress-hint">{LAB_PROGRESS_HINTS[assignmentProgress]}</p>
        </div>
      ) : null}
      <header className="ide-header">
        <div className="ide-header-left">
          <Link to={backTo} className="ide-back">
            ← {backLabel}
          </Link>
          <div className="ide-title-block">
            <p className="ide-eyebrow">{eyebrow}</p>
            <h1 className="ide-title">{title}</h1>
          </div>
        </div>
        <div className="ide-header-actions">
          {headerExtra}
          {persistLocally ? (
          <span className="ide-save-status-wrap" aria-live="polite">
            <span
              className={`ide-save-status ide-save-status--${saveStatus}`}
              title={
                saveStatus === "error"
                  ? "Could not write to browser storage (quota or private mode)."
                  : "Your code is saved on this device."
              }
            >
              {saveStatus === "saved"
                ? "Saved locally"
                : saveStatus === "unsaved"
                  ? "Unsaved changes"
                  : saveStatus === "saving"
                    ? "Saving…"
                    : "Save failed"}
            </span>
            {saveStatus === "error" ? (
              <button type="button" className="ide-save-retry btn secondary ide-btn tight" onClick={retrySave}>
                Retry
              </button>
            ) : null}
          </span>
          ) : null}
          <span className={`ide-status ide-status--${phase}`} aria-live="polite">
            {loadingRuntime
              ? "Loading Python…"
              : phase === "ready"
                ? "Ready"
                : phase === "running"
                  ? "Running"
                  : phase === "awaiting_input"
                    ? "Waiting for input"
                    : "-"}
          </span>
          <button
            type="button"
            className="btn secondary ide-btn"
            onClick={() => clearConsole()}
            disabled={running}
          >
            Clear console
          </button>
          {!readOnly && workspaceResetTarget ? (
            <button
              type="button"
              className="btn secondary ide-btn"
              onClick={() => setResetConfirmOpen(true)}
              disabled={resetBusy || running}
            >
              Start over
            </button>
          ) : null}
          <button
            type="button"
            className="btn secondary ide-btn"
            onClick={() => stop()}
            disabled={!running || loadingRuntime}
          >
            Stop
          </button>
          <button
            type="button"
            className="btn primary ide-btn"
            onClick={() => onRun()}
            disabled={running || loadingRuntime || !canUseSharedMemory}
          >
            Run
          </button>
          {!readOnly ? (
          <button
            type="button"
            className="btn secondary ide-btn"
            onClick={() => void onMarkDone()}
            disabled={markDoneDisabled}
            title={markDoneHint}
          >
            <FontAwesomeIcon icon={faCircleCheck} />{" "}
            {markDoneBusy ? (lessonTestMarkDone ? "Checking…" : "Submitting…") : "Check My Work"}
          </button>
          ) : null}
        </div>
      </header>

      <p className="ide-account muted small">
        {identity?.email ? `${identity.email} · ` : ""}
        {workspaceNote}
      </p>

      {runtimeError ? <div className="warn-banner ide-banner">{runtimeError}</div> : null}

      <div className="ide-panels">
        <section className="ide-editor-panel" aria-label="Python editor">
          <div className="ide-panel-label">Editor</div>
          <div ref={editorHostRef} className="ide-editor-mount" />
        </section>
        <IdeOutputPanel
          lines={lines}
          phase={phase}
          inputPrompt={inputPrompt}
          inputDraft={inputDraft}
          onInputDraftChange={setInputDraft}
          onSubmitInput={onSubmitConsoleInput}
          consoleInputRef={consoleInputRef}
          clearConsoleOnRun={clearConsoleOnRun}
          onClearConsoleOnRunChange={setClearConsoleOnRun}
          outputTab={outputTab}
          onOutputTabChange={setOutputTab}
          turtleHtml={turtleHtml}
          turtleTabVisible={turtleTabVisible}
        />
        <StudentIdeTourPanel steps={tourSteps} />
      </div>

      {markDoneModal ? (
        <div className="ide-modal-root" role="dialog" aria-modal="true" aria-labelledby="ide-mark-done-title">
          <button
            type="button"
            className="ide-modal-backdrop"
            aria-label="Close dialog"
            onClick={() => setMarkDoneModal(null)}
          />
          <div className="ide-modal-card card">
            {markDoneModal.kind === "error" ? (
              <>
                <h2 id="ide-mark-done-title" className="ide-modal-title">
                  Could not submit
                </h2>
                <p className="ide-modal-body">{markDoneModal.message}</p>
              </>
            ) : (
              <>
                <h2 id="ide-mark-done-title" className="ide-modal-title">
                  {markDoneModal.result.outcome === "passed" ? "🏆 Nice work!" : "🧩 Not quite yet"}
                </h2>
                <p
                  className={`ide-modal-outcome ide-modal-outcome--${markDoneModal.result.outcome}`}
                  aria-live="polite"
                >
                  {markDoneModal.result.summary}
                </p>
                <ul className="ide-modal-checks">
                  {markDoneModal.result.checks.map((c) => (
                    <li key={c.id} className={c.passed ? "ide-check-pass" : "ide-check-fail"}>
                      <span className="ide-check-mark" aria-hidden>
                        {c.passed ? "✓" : "✗"}
                      </span>
                      {c.label}
                    </li>
                  ))}
                </ul>
                {markDoneModal.submissionSaveError ? (
                  <p className="warn-banner ide-modal-body" role="alert">
                    {markDoneModal.submissionSaveError}
                  </p>
                ) : markDoneSubmissionTarget ? (
                  <p className="muted small ide-modal-engine">Your attempt was saved for this assignment.</p>
                ) : null}
                <p className="muted small ide-modal-engine">
                  {lessonTestMarkDone
                    ? "Lesson checks ran privately in your browser. Test definitions are never shown here. Only outcomes and hints."
                    : `Validator: ${markDoneModal.result.validationEngine} · submission ${markDoneModal.result.submissionId}`}
                </p>
              </>
            )}
            <div className="ide-modal-actions">
              <button type="button" className="btn primary" onClick={() => setMarkDoneModal(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {resetConfirmOpen ? (
        <div className="ide-modal-root" role="dialog" aria-modal="true" aria-labelledby="ide-reset-title">
          <button
            type="button"
            className="ide-modal-backdrop"
            aria-label="Close dialog"
            onClick={() => !resetBusy && setResetConfirmOpen(false)}
          />
          <div className="ide-modal-card card">
            <h2 id="ide-reset-title" className="ide-modal-title">
              Start over?
            </h2>
            <p className="ide-modal-body">
              Your changes will be undone. Your project will go back to the original starter code from your
              teacher.
            </p>
            <div className="ide-modal-actions">
              <button
                type="button"
                className="btn secondary"
                onClick={() => setResetConfirmOpen(false)}
                disabled={resetBusy}
              >
                Cancel
              </button>
              <button type="button" className="btn primary" onClick={() => void onConfirmReset()} disabled={resetBusy}>
                {resetBusy ? "Resetting…" : "Reset project"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
